import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    Checkbox,
    Skeleton,
} from "@mrflark/ui";
import {
    Film,
} from "lucide-preact";
import type {
    ThumbnailSize,
} from "../../stores/manageStore.ts";
import {
    TagPill,
} from "../tags/TagPill.tsx";

const THUMBNAIL_SIZE_STYLES: Record<ThumbnailSize, string> = {
    small: "grid-cols-[repeat(auto-fill,minmax(100px,1fr))]",
    medium: "grid-cols-[repeat(auto-fill,minmax(150px,1fr))]",
    large: "grid-cols-[repeat(auto-fill,minmax(200px,1fr))]",
};

type MediaGridProps = {
    items: MediaItem[];
    selectedIds: Set<string>;
    onToggleSelect: (id: string) => void;
    onSelectAll: () => void;
    viewMode: "grid" | "list";
    thumbnailSize?: ThumbnailSize;
    onItemClick: (item: MediaItem) => void;
    onItemHover?: (item: MediaItem, e: MouseEvent) => void;
    onItemLeave?: () => void;
    onMouseMove?: (e: MouseEvent) => void;
    isLoading?: boolean;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    onSort?: (column: string) => void;
    focusedIndex?: number;
};

export const MediaGrid = ({
    items,
    selectedIds,
    onToggleSelect,
    onSelectAll,
    viewMode,
    thumbnailSize = "medium",
    onItemClick,
    onItemHover,
    onItemLeave,
    onMouseMove,
    isLoading,
    sortBy,
    sortOrder,
    onSort,
    focusedIndex = -1,
}: MediaGridProps) => {
    const allSelected = items.length > 0 &&
        items.every((item) => selectedIds.has(item.id));
    const someSelected = items.some((item) => selectedIds.has(item.id));

    if (isLoading) {
        return viewMode === "grid"
            ? (
                <div
                    className={`grid gap-4 ${
                        THUMBNAIL_SIZE_STYLES[thumbnailSize]
                    }`}
                >
                    {Array.from({
                        length: 12,
                    }).map((_, i) => (
                        <Skeleton key={i} className="aspect-square" />
                    ))}
                </div>
            )
            : (
                <div className="space-y-2">
                    {Array.from({
                        length: 8,
                    }).map((_, i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                    ))}
                </div>
            );
    }

    if (items.length === 0) {
        return (
            <div className="text-center py-12 text-text-secondary">
                No items found
            </div>
        );
    }

    if (viewMode === "grid") {
        return (
            <div
                className={`grid gap-4 ${THUMBNAIL_SIZE_STYLES[thumbnailSize]}`}
            >
                {items.map((item, index) => (
                    <GridItem
                        key={item.id}
                        item={item}
                        isSelected={selectedIds.has(item.id)}
                        isFocused={index === focusedIndex}
                        onToggleSelect={() => onToggleSelect(item.id)}
                        onHover={onItemHover}
                        onLeave={onItemLeave}
                        onMouseMove={onMouseMove}
                    />
                ))}
            </div>
        );
    }

    // List view
    return (
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead className="bg-bg-tertiary sticky top-0">
                    <tr>
                        <th className="p-3 text-left w-10">
                            <Checkbox
                                checked={allSelected}
                                indeterminate={someSelected && !allSelected}
                                onChange={onSelectAll}
                            />
                        </th>
                        <th className="p-3 text-left w-16">Preview</th>
                        <SortableHeader
                            column="id"
                            label="ID"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={onSort}
                        />
                        <SortableHeader
                            column="media_type"
                            label="Type"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={onSort}
                        />
                        <th className="p-3 text-left">Tags</th>
                        <SortableHeader
                            column="views"
                            label="Views"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={onSort}
                        />
                        <SortableHeader
                            column="votes"
                            label="Votes"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={onSort}
                        />
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => (
                        <ListItem
                            key={item.id}
                            item={item}
                            isSelected={selectedIds.has(item.id)}
                            onToggleSelect={() => onToggleSelect(item.id)}
                            onClick={() => onItemClick(item)}
                            onHover={onItemHover}
                            onLeave={onItemLeave}
                            onMouseMove={onMouseMove}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Grid item component
type GridItemProps = {
    item: MediaItem;
    isSelected: boolean;
    isFocused?: boolean;
    onToggleSelect: () => void;
    onHover?: (item: MediaItem, e: MouseEvent) => void;
    onLeave?: () => void;
    onMouseMove?: (e: MouseEvent) => void;
};

const GridItem = ({
    item,
    isSelected,
    isFocused,
    onToggleSelect,
    onHover,
    onLeave,
    onMouseMove,
}: GridItemProps) => {
    const imageUrl = item.thumbnail_url || item.media_url;

    const borderClass = isFocused
        ? "ring-2 ring-accent border-accent"
        : isSelected
        ? "border-accent shadow-lg scale-[1.02]"
        : "border-transparent hover:border-border";

    return (
        <div
            data-testid="library-grid-item"
            className={`
        relative aspect-square bg-bg-tertiary rounded-lg overflow-hidden cursor-pointer
        border-2 transition-all duration-200
        ${borderClass}
      `}
            onClick={onToggleSelect}
            onMouseEnter={(e) => onHover?.(item, e)}
            onMouseLeave={onLeave}
            onMouseMove={onMouseMove}
        >
            {/* Thumbnail */}
            {imageUrl
                ? (
                    <img
                        src={imageUrl}
                        alt={item.id}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                )
                : (
                    <div className="w-full h-full flex items-center justify-center text-text-muted">
                        No preview
                    </div>
                )}

            {/* Checkbox overlay */}
            <div
                data-testid="library-grid-item-checkbox"
                className="absolute top-2 left-2 bg-white/90 rounded p-0.5"
                onClick={(e) => e.stopPropagation()}
            >
                <Checkbox checked={isSelected} onChange={onToggleSelect} />
            </div>

            {/* Video indicator */}
            {item.media_type === "video" && (
                <div className="absolute bottom-2 right-2 bg-black/60 rounded p-1">
                    <Film size={16} className="text-white" />
                </div>
            )}

            {/* Tags overlay */}
            {item.tags.length > 0 && (
                <div className="absolute bottom-2 left-2 flex flex-wrap gap-1 max-w-[calc(100%-3rem)]">
                    {item.tags.slice(0, 3).map((tag) => (
                        <TagPill key={tag} tag={tag} size="sm" />
                    ))}
                    {item.tags.length > 3 && (
                        <span className="px-1.5 py-0.5 text-xs bg-bg-secondary/90 rounded">
                            +{item.tags.length - 3}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

// List item component
type ListItemProps = {
    item: MediaItem;
    isSelected: boolean;
    onToggleSelect: () => void;
    onClick: () => void;
    onHover?: (item: MediaItem, e: MouseEvent) => void;
    onLeave?: () => void;
    onMouseMove?: (e: MouseEvent) => void;
};

const ListItem = ({
    item,
    isSelected,
    onToggleSelect,
    onClick,
    onHover,
    onLeave,
    onMouseMove,
}: ListItemProps) => {
    const imageUrl = item.thumbnail_url || item.media_url;

    return (
        <tr
            className={`
        border-b border-border cursor-pointer transition-colors
        ${isSelected ? "bg-accent/10" : "hover:bg-bg-tertiary"}
      `}
            onClick={onClick}
            onMouseEnter={(e) => onHover?.(item, e)}
            onMouseLeave={onLeave}
            onMouseMove={onMouseMove}
        >
            <td className="p-3" onClick={(e) => e.stopPropagation()}>
                <Checkbox checked={isSelected} onChange={onToggleSelect} />
            </td>
            <td className="p-3">
                {imageUrl
                    ? (
                        <img
                            src={imageUrl}
                            alt={item.id}
                            className="w-12 h-12 object-cover rounded"
                            loading="lazy"
                        />
                    )
                    : (
                        <div className="w-12 h-12 bg-bg-tertiary rounded flex items-center justify-center text-text-muted text-xs">
                            -
                        </div>
                    )}
            </td>
            <td className="p-3 font-mono text-sm">{item.id}</td>
            <td className="p-3">
                {item.media_type === "video"
                    ? (
                        <span className="flex items-center gap-1">
                            <Film size={14} /> Video
                        </span>
                    )
                    : (
                        "Image"
                    )}
            </td>
            <td className="p-3">
                <div className="flex flex-wrap gap-1">
                    {item.tags.slice(0, 5).map((tag) => (
                        <TagPill key={tag} tag={tag} size="sm" />
                    ))}
                    {item.tags.length > 5 && (
                        <span className="text-text-muted text-sm">
                            +{item.tags.length - 5}
                        </span>
                    )}
                </div>
            </td>
            <td className="p-3">{item.views}</td>
            <td className="p-3">{item.votes}</td>
        </tr>
    );
};

// Sortable header component
type SortableHeaderProps = {
    column: string;
    label: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    onSort?: (column: string) => void;
};

const SortableHeader = ({
    column,
    label,
    sortBy,
    sortOrder,
    onSort,
}: SortableHeaderProps) => {
    const isActive = sortBy === column;

    return (
        <th
            className="p-3 text-left cursor-pointer hover:bg-border/50 transition-colors select-none"
            onClick={() => onSort?.(column)}
        >
            <span className="flex items-center gap-1">
                {label}
                {isActive && (
                    <span className="text-accent">
                        {sortOrder === "asc" ? "▲" : "▼"}
                    </span>
                )}
            </span>
        </th>
    );
};
